<template>
  <q-page class="row justify-center items-center column add-video">
    <ProgressModal :progress="progress" :isProgress="isProgress" />

    <BackgroundCircles />

    <div class="q-pa-md add-video__form">
      <q-stepper
        v-model="step"
        ref="stepper"
        color="primary"
        animated
        keep-alive
      >
        <q-step
          class="q-step-uploader"
          :name="1"
          :title="$t('addMedia.addVideoTitle')"
          icon="videocam"
          :done="step > 1"
          style="min-height: 200px"
        >
          <q-icon name="videocam" class="step-content__icon" />

          <FilePicker
            v-if="!reload"
            :label="$t('addMedia.addFiles')"
            ref="FilePickerRef"
            fileType="video"
            :filePickerThumbs="filePickerThumbs"
            @filesSelected="setMedia"
          />
        </q-step>

        <q-step
          :name="2"
          :title="$t('common.completeData')"
          icon="settings"
          :done="step > 2"
          style="min-height: 200px"
        >
          <InformationForm
            @setCity="(val) => (selectedCity = val)"
            @setCountry="(val) => (selectedCountry = val)"
            @setEvent="(val) => (selectedEvent = val)"
          />
        </q-step>

        <q-step
          :name="3"
          :title="$t('common.summary')"
          icon="add_comment"
          style="min-height: 200px"
        >
          <StepperSummary
            :selectedCity="selectedCity"
            :selectedCountry="selectedCountry"
            :selectedEvent="selectedEvent"
            :mediaCount="selectedMedia.length"
            :mediaTypeLabel="$t('common.videosCount')"
          />
        </q-step>

        <template v-slot:navigation>
          <q-stepper-navigation class="q-mb-lg">
            <q-btn
              @click="handleClick"
              :disable="isNextDisabled"
              color="primary"
              v-ripple
              :label="step === 3 ? $t('common.submit') : $t('common.next')"
            />
            <q-btn
              v-if="step > 1"
              flat
              v-ripple
              color="primary"
              @click="$refs.stepper.previous()"
              :label="$t('common.return')"
              class="q-ml-sm"
            />
          </q-stepper-navigation>
        </template>
      </q-stepper>
    </div>
  </q-page>
</template>

<script>
import FilePicker from './components/FilePicker.vue';
import api from '@/api/api.js';
import { mapState, mapActions } from 'vuex';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import InformationForm from './components/InformationForm.vue';
import ProgressModal from './components/ProgressModal.vue';
import BackgroundCircles from './components/BackgroundCircles.vue';
import StepperSummary from './components/StepperSummary.vue';

export default {
  name: 'AddVideo',
  components: {
    FilePicker,
    ProgressModal,
    InformationForm,
    BackgroundCircles,
    StepperSummary,
  },
  data() {
    return {
      step: 1,
      uuidValue: '',
      selectedCountry: '',
      selectedCity: '',
      selectedEvent: '',
      filePickerThumbs: false,
      selectedMedia: [],
      reload: false,
      progress: 0,
      isProgress: false,
      success: true,
      maxCountTry: 5,
      errorTrying: 0,
      videoCount: 0,
      baseTimeOut: 1000,
      videoErrors: [],
      isNotified: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      citiesList: (state) => state.global.citiesList,
      countriesList: (state) => state.global.countriesList,
      selectEventsList: (state) => state.global.selectEventsList,
    }),
    isNextDisabled() {
      if (this.step === 1) {
        return !this.selectedMedia.length;
      }

      if (this.step > 2) {
        return !this.selectedMedia.length || !this.selectedEvent;
      }

      return false;
    },
  },
  methods: {
    ...mapActions('global', [
      'loadFormData',
      'saveFormData',
      'getCountries',
      'getCities',
      'fetchEventsForSelect',
    ]),
    setMedia(data) {
      this.selectedMedia = [...data];
    },
    generateUniqueId() {
      this.uuidValue = uuidv4();
    },
    clearAllData() {
      this.errorTrying = 0;
      this.generateUniqueId();
      this.selectedMedia = [];
      this.isProgress = false;
      this.progress = 0;
      this.$refs.FilePickerRef.addFromParent(this.selectedMedia);
    },
    beforeUnloadHandler(e) {
      if (this.selectedMedia.length > 0) {
        e.preventDefault();
        return '';
      }
    },
    goToLogin() {
      this.$router.push('/login');
    },
    splitToChunks(array) {
      const maxVideosInPackage = 5;
      const count = array.length / maxVideosInPackage;
      const parts = Math.ceil(count);
      let result = [];
      for (let i = parts; i > 0; i--) {
        result.push(array.splice(0, Math.ceil(array.length / i)));
      }

      return result;
    },
    asyncReq(data, videoPackages, progressPart, countProgress) {
      if (this.isNotified) {
        return;
      }

      const vm = this;
      return new Promise((resolve) => {
        api
          .sendVideoData(data)
          .then((response) => {
            const {
              data: { data: uploadedVideos },
            } = response;
            const copy = _.clone(vm.selectedMedia);
            const videosWithSuccess = uploadedVideos.filter(
              (element) => element.message === 'videoUpload.queue.added'
            );
            videosWithSuccess.forEach((item) => {
              const index = copy.findIndex(
                (element) => element.name === item.video
              );
              if (index !== -1) {
                copy.splice(index, 1);
              }
            });
            vm.selectedMedia = _.clone(copy);
            if (!vm.errorTrying) {
              if (countProgress === 0) {
                vm.progress = vm.progress + progressPart / 2;
              } else {
                vm.progress = vm.progress + progressPart;
              }
            }
            resolve();
          })
          .catch((error) => {
            if (error.response && error.response.status === 429) {
              if (!this.isNotified) {
                this.isNotified = true;
                this.$q.notify({
                  progress: true,
                  message: this.$t('error.uploadsRestriction'),
                  color: 'info',
                  icon: 'info',
                  position: 'bottom',
                  timeout: 60000,
                });

                setTimeout(() => {
                  this.isNotified = false;
                }, 60000);
              }
            }
            resolve();
          });
      });
    },

    async handleClick() {
      const vm = this;

      if (this.step === 3) {
        if (!this.user) return;
        if (this.selectedEvent && this.selectedMedia.length) {
          const completeData = new FormData();
          completeData.append('upload_id', this.uuidValue);
          completeData.append('wp_run_id', this.selectedEvent.id);

          const formData = {
            city: this.selectedEvent.assign_city,
            country: this.selectedCountry.id,
            event: this.selectedEvent.id,
          };
          this.saveFormData(formData);

          const copyVideos = [...vm.selectedMedia];
          vm.videoCount = copyVideos.length;
          completeData.append('videos_count', vm.videoCount);

          const videoPackages = vm.splitToChunks(copyVideos);
          const progressPart = 1 / videoPackages.length;

          vm.videoErrors = [...vm.selectedMedia];
          vm.isProgress = true;
          let countProgress = 0;

          vm.processSendingVideos(
            completeData,
            videoPackages,
            progressPart,
            countProgress
          );
        } else {
          if (!this.selectedMedia.length) {
            this.step = 1;
          }
          if (!this.selectedEvent) {
            this.step = 2;
          }

          this.$q.notify({
            icon: 'warning',
            color: 'negative',
            message: this.$t('validation.completeAllFields'),
          });
        }
      } else {
        this.$refs.stepper.next();
      }
    },

    async processSendingVideos(
      completeData,
      videoPackages,
      progressPart,
      countProgress,
      isError
    ) {
      const vm = this;

      if (countProgress === 0 && !isError) {
        vm.progress = vm.progress + progressPart / 2;
      }

      for (countProgress; countProgress < videoPackages.length; ) {
        const p = videoPackages[countProgress];

        completeData.delete('videos[]');
        p.forEach((file) => {
          completeData.append('videos[]', file);
        });
        await vm.asyncReq(
          completeData,
          videoPackages,
          progressPart,
          countProgress
        );

        countProgress++;
      }

      if (vm.abortRetries) {
        return;
      }

      const clone = [...vm.selectedMedia];
      if (clone.length > 0 && vm.errorTrying < vm.maxCountTry) {
        vm.errorTrying++;
        const time = vm.baseTimeOut * vm.errorTrying;
        const packages = vm.splitToChunks(clone);

        let isError = true;
        setTimeout(() => {
          vm.processSendingVideos(
            completeData,
            packages,
            progressPart,
            0,
            isError
          );
        }, time);

        return;
      }

      if (vm.errorTrying === vm.maxCountTry && clone.length) {
        vm.errorTrying = 0;
        vm.progress = 0;
        vm.selectedMedia = [...clone];
        const packages = vm.splitToChunks(clone);
        vm.$q.notify({
          icon: 'error',
          color: 'negative',
          message: `${vm.$t('error.catchedError')} ${vm.$t(
            'error.addedVideosInfo'
          )} ${vm.videoCount - vm.selectedMedia.length}/${vm.videoCount}`,
          timeout: 0,
          actions: [
            {
              label: vm.$t('error.renewSendVideos'),
              color: 'white',
              handler: () => {
                vm.processSendingVideos(
                  completeData,
                  packages,
                  progressPart,
                  0
                );
              },
            },
            {
              label: vm.$t('error.sendLater'),
              color: 'white',
              handler: async () => {
                const cloneArray = [...vm.selectedMedia];
                vm.$refs.FilePickerRef.addFromParent(cloneArray);

                vm.step = 1;
                vm.isProgress = false;
                vm.progress = 0;
                vm.errorTrying = 0;
              },
            },
          ],
        });
        return;
      }
      vm.progress = 1;
      setTimeout(() => {
        this.$q.notify({
          icon: 'done',
          color: 'positive',
          message: this.$t('success.videoAdded'),
          timeout: 0,
          actions: [{ icon: 'close', color: 'white' }],
        });
        this.clearAllData();
        this.step = 1;
      }, 2000);
    },
  },
  mounted() {
    if (this.user && (!Array.isArray(this.selectEventsList) || this.selectEventsList.length < 1)) {
      this.fetchEventsForSelect();
    }

    if (!Array.isArray(this.citiesList) || this.citiesList.length < 1) {
      this.getCities();
    }

    if (!Array.isArray(this.countriesList) || this.countriesList.length < 1) {
      this.getCountries();
    }

    this.loadFormData();
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  },
  created() {
    this.generateUniqueId();
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  },
};
</script>

<style lang="scss" scoped>
.add-video {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding-top: 150px;
  &__button {
    width: 160px;
  }

  &__form {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: inherit;
    z-index: 3;
    position: relative;
  }
}

.add-video {
  padding-top: 150px;
}
::v-deep {
  .q-uploader__header-content {
    > div {
      a {
        &:nth-of-type(3) {
          display: none;
        }
      }
    }
  }

  .q-stepper {
    width: 100%;
    max-width: 800px;
    -webkit-box-shadow: 0px 0px 10px 0px #ffffff;
    box-shadow: 0px 0px 10px 0px #ffffff;
    @media (max-width: 548px) {
      min-width: 90%;
      &__label {
        display: none;
      }
      .q-stepper__dot:before {
        display: none;
      }
    }
    @media (max-width: 380px) {
      min-width: 100%;
      width: 100%;
    }
  }
  .q-step-uploader {
    .q-stepper {
      &__step {
        &-inner {
          padding-top: 0;
          padding-left: 0;
          padding-right: 0;
          display: flex;
          justify-content: center;
          flex-direction: column;
          align-items: center;
          position: relative;
          .step-content__icon {
            display: block;
            font-size: 2.5rem;
            color: #fb205655;
            position: absolute;
            top: 1rem;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1;
            @media (max-width: 400px) {
              display: none;
            }
          }
          .q-uploader {
            max-width: 100%;
            box-shadow: none;
            @media (min-width: 548px) {
              &__list {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                padding: 24px;
              }
              &__file {
                margin: 6px;
                &--img {
                  height: 100px;
                  width: 100px;
                }
              }
              &__header {
                &-content {
                  padding: 32px;
                }
              }
            }
            &__header {
              &-content {
                padding: 18px;
              }
            }
          }
        }
      }
    }
  }
}

@media (max-width: 450px) {
  .add-video {
    &__title {
      font-size: 22px;
      padding: 8px;
      margin: 8px 12px;
      top: 10px;
      width: 90%;
    }
  }
}
</style>
